// ============================================================================
// DECLARATION ENGINE REPOSITORY
// ARQUIVO: src/services/DeclarationEngineRepository.js
// RESPONSABILIDADE: Operações Firestore do DeclarationEngine (templates, docs).
// Criado: Fase 6.3 — mover Firestore direto de DeclarationEngine.jsx para repositório.
// ============================================================================

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../config/Config";

const APP_ID = FIREBASE_CONFIG?.projectId || "portalrda-f312d";

/** Carrega template de declaração (snapshot único). */
export async function loadDeclarationTemplate() {
  const tplDoc = await getDoc(
    doc(
      db,
      "artifacts",
      APP_ID,
      "public",
      "config",
      "declaration_templates",
      "tpl_hipossuficiencia_v2",
    ),
  );
  return tplDoc.exists() ? tplDoc.data() : null;
}

/**
 * Salva/sobrescreve o template de declaração na nuvem (Mestre).
 * Espelha setProcurationMaster do TemplateRepository, adaptado à declaração.
 * @param {object} payload - { structure, updatedBy }
 */
export async function saveDeclarationTemplate(payload) {
  const ref = doc(
    db,
    "artifacts",
    APP_ID,
    "public",
    "config",
    "declaration_templates",
    "tpl_hipossuficiencia_v2",
  );
  const data = {
    structure: JSON.parse(JSON.stringify(payload?.structure || [])),
    updatedAt: serverTimestamp(),
    updatedBy: payload?.updatedBy || null,
  };
  const existing = await getDoc(ref);
  if (existing.exists()) {
    await updateDoc(ref, data);
  } else {
    await addDoc(
      collection(
        db,
        "artifacts",
        APP_ID,
        "public",
        "config",
        "declaration_templates",
      ),
      {
        ...data,
        id: "tpl_hipossuficiencia_v2",
        createdAt: serverTimestamp(),
      },
    );
  }
  return true;
}

/** Assina (tempo real) o template de declaração — espelha subscribeProcurationMasterV1. */
export function subscribeDeclarationTemplate(callback) {
  const ref = doc(
    db,
    "artifacts",
    APP_ID,
    "public",
    "config",
    "declaration_templates",
    "tpl_hipossuficiencia_v2",
  );
  return onSnapshot(ref, (snap) => {
    const data = snap.exists() ? snap.data() : null;
    callback(data && Array.isArray(data.structure) ? data : null);
  });
}

/** Atualiza estrutura de documento. */
export async function updateDocumentStructure(
  docId,
  structure,
  reasonCodes,
  clauseOverrides,
  ramoDireito,
  actorName,
) {
  await updateDoc(
    doc(
      collection(db, "artifacts", APP_ID, "public", "data", "documents"),
      docId,
    ),
    {
      structure: JSON.parse(JSON.stringify(structure)),
      reasonCodes,
      clauseOverrides,
      ramoDireito,
      updatedAt: serverTimestamp(),
      updatedBy: actorName,
    },
  );
}

/** Busca workspace do cliente. */
export async function findClientWorkspace(clientId) {
  const wsSnaps = await getDocs(
    query(
      collection(db, "artifacts", APP_ID, "public", "data", "autos_workspaces"),
      where("clientId", "==", clientId),
    ),
  );
  return !wsSnaps.empty ? wsSnaps.docs[0] : null;
}

/** Busca declaração existente do cliente (mais recente). */
export async function findExistingDeclaration(clientId) {
  const declSnaps = await getDocs(
    query(
      collection(db, "artifacts", APP_ID, "public", "data", "documents"),
      where("clientId", "==", clientId),
      where("type", "==", "DECLARATION"),
    ),
  );
  if (declSnaps.empty) return null;
  const sorted = declSnaps.docs
    .map((d) => ({ id: d.id, data: d.data() }))
    .sort((a, b) => {
      const ta = a.data?.updatedAt?.toMillis?.() || 0;
      const tb = b.data?.updatedAt?.toMillis?.() || 0;
      return tb - ta;
    });
  return sorted[0];
}

/** Salva declaração (cria ou atualiza). */
export async function saveDeclaration(payload, clientId, existingId) {
  const docsCol = collection(
    db,
    "artifacts",
    APP_ID,
    "public",
    "data",
    "documents",
  );
  let docId = existingId;
  const enriched = {
    ...payload,
    updatedAt: payload.updatedAt || serverTimestamp(),
  };

  if (docId) {
    await updateDoc(doc(docsCol, docId), enriched);
  } else {
    const existingSnap = await getDocs(
      query(
        docsCol,
        where("clientId", "==", clientId),
        where("type", "==", "DECLARATION"),
      ),
    );
    if (!existingSnap.empty) {
      const sorted = existingSnap.docs
        .map((d) => ({ id: d.id, ts: d.data()?.updatedAt?.toMillis?.() || 0 }))
        .sort((a, b) => b.ts - a.ts);
      docId = sorted[0].id;
      await updateDoc(doc(docsCol, docId), payload);
    } else {
      const ref = await addDoc(docsCol, {
        ...payload,
        id: "",
        createdAt: serverTimestamp(),
        publicationVersion: 0,
      });
      await updateDoc(ref, { id: ref.id });
      docId = ref.id;
    }
  }
  return docId;
}

/** Publica declaração. */
export async function publishDeclaration(docId, data) {
  const docsCol = collection(
    db,
    "artifacts",
    APP_ID,
    "public",
    "data",
    "documents",
  );
  await updateDoc(doc(docsCol, docId), {
    ...data,
    updatedAt: data.updatedAt || serverTimestamp(),
  });
}

/** Exclui publicação do acervo. */
export async function deleteDeclarationPublication(docId) {
  const docsCol = collection(
    db,
    "artifacts",
    APP_ID,
    "public",
    "data",
    "documents",
  );
  await deleteDoc(doc(docsCol, docId));
}
